import { useEffect, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ExternalLink } from 'lucide-react';
import { ITEM_TYPES } from '@mkcore/knowledge/schema';
import { Badge, Card, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { formatDate } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { useDebounced } from '@/features/search/search';
import { AskPanel } from '@/features/knowledge/panels';
import { searchKnowledge, sourcesQuery, spacesQuery } from '@/features/knowledge/queries';
import { KindIcon, Snippet, SpaceLabel, TypeBadge } from '@/features/knowledge/ui';

const text = z
  .union([z.string(), z.number()])
  .optional()
  .transform((v) => (v === undefined ? undefined : String(v)));
const search = z.object({
  q: text,
  mode: z.enum(['hybrid', 'fts', 'vector']).optional(),
  kind: z.enum(['item', 'document']).optional(),
  type: z.enum(ITEM_TYPES).optional(),
  space: z.string().optional(),
  source: z.string().optional(),
  tag: text,
  path: text,
  from: z.string().optional(),
  to: z.string().optional(),
  sort: z.enum(['relevance', 'date']).optional(),
  history: z.boolean().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/knowledge/search')({ validateSearch: search, component: SearchPage });

/**
 * One search box over all knowledge (CO-1120, CO-1100, CO-1138, CO-1089): hybrid by default, with filters on space,
 * source, kind, type, tag, folder and time, snippets with the words found, the source and a deeplink; and the question
 * field next to it (CO-1087), in the same scope.
 */
function SearchPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const [typed, setTyped] = useState(s.q ?? '');
  const q = useDebounced(typed, 300);
  useEffect(() => {
    if ((q || undefined) !== s.q) set({ q: q || undefined });
  }, [q]);
  const spaces = useQuery(spacesQuery()).data?.items ?? [];
  const canSources = useCan('knowledge:read');
  const sources = useQuery({ ...sourcesQuery(), enabled: canSources }).data?.items ?? [];
  const found = useQuery(
    searchKnowledge({
      q: s.q ?? '',
      mode: s.mode,
      ...(s.kind && { kind: [s.kind] }),
      ...(s.type && { type: [s.type] }),
      spaceId: s.space,
      sourceId: s.source,
      tag: s.tag,
      path: s.path,
      from: s.from,
      to: s.to,
      sort: s.sort,
      history: s.history,
      limit: 30,
    }),
  );
  const r = found.data;
  return (
    <>
      <PageHeader title={t('knowledge.search.title')} description={t('knowledge.search.description')} />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <section className="grid content-start gap-3">
          <Field label={t('knowledge.search.query')}>{(p) => <Input {...p} type="search" autoFocus value={typed} placeholder={t('knowledge.search.placeholder')} onChange={(e) => setTyped(e.target.value)} />}</Field>
          <div className="flex flex-wrap items-end gap-2" role="group" aria-label={t('knowledge.search.filters')}>
            <Field label={t('knowledge.search.scope')} className="w-52">
              {(p) => (
                <NativeSelect {...p} value={s.space ?? ''} onChange={(e) => set({ space: e.target.value || undefined })}>
                  <option value="">{t('knowledge.allSpaces')}</option>
                  {spaces.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('knowledge.search.kind')} className="w-36">
              {(p) => (
                <NativeSelect {...p} value={s.kind ?? ''} onChange={(e) => set({ kind: (e.target.value || undefined) as Search['kind'] })}>
                  <option value="">{t('common.all')}</option>
                  <option value="item">{t('knowledge.items')}</option>
                  <option value="document">{t('knowledge.documents')}</option>
                </NativeSelect>
              )}
            </Field>
            {s.kind !== 'item' && !!sources.length && (
              <Field label={t('knowledge.search.source')} className="w-44">
                {(p) => (
                  <NativeSelect {...p} value={s.source ?? ''} onChange={(e) => set({ source: e.target.value || undefined })}>
                    <option value="">{t('common.all')}</option>
                    {sources.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            {s.kind !== 'document' && (
              <Field label={t('knowledge.item.fields.type')} className="w-36">
                {(p) => (
                  <NativeSelect {...p} value={s.type ?? ''} onChange={(e) => set({ type: (e.target.value || undefined) as Search['type'] })}>
                    <option value="">{t('common.all')}</option>
                    {ITEM_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {t(`knowledge.types.${x}`)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            )}
            <Field label={t('knowledge.filter.tag')} className="w-32">
              {(p) => <Input {...p} value={s.tag ?? ''} onChange={(e) => set({ tag: e.target.value || undefined })} />}
            </Field>
            <Field label={t('knowledge.item.fields.path')} className="w-36">
              {(p) => <Input {...p} value={s.path ?? ''} onChange={(e) => set({ path: e.target.value || undefined })} />}
            </Field>
            <Field label={t('knowledge.search.from')} className="w-36">
              {(p) => <Input {...p} type="date" value={s.from ?? ''} onChange={(e) => set({ from: e.target.value || undefined })} />}
            </Field>
            <Field label={t('knowledge.search.to')} className="w-36">
              {(p) => <Input {...p} type="date" value={s.to ?? ''} onChange={(e) => set({ to: e.target.value || undefined })} />}
            </Field>
            <Field label={t('knowledge.search.mode')} className="w-36">
              {(p) => (
                <NativeSelect {...p} value={s.mode ?? 'hybrid'} onChange={(e) => set({ mode: e.target.value === 'hybrid' ? undefined : (e.target.value as Search['mode']) })}>
                  {(['hybrid', 'fts', 'vector'] as const).map((m) => (
                    <option key={m} value={m}>
                      {t(`knowledge.search.modes.${m}`)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
            <Field label={t('knowledge.search.sort')} className="w-36">
              {(p) => (
                <NativeSelect {...p} value={s.sort ?? 'relevance'} onChange={(e) => set({ sort: e.target.value === 'relevance' ? undefined : 'date' })}>
                  <option value="relevance">{t('knowledge.search.byRelevance')}</option>
                  <option value="date">{t('knowledge.search.byDate')}</option>
                </NativeSelect>
              )}
            </Field>
          </div>
          {!s.q ? (
            <EmptyState title={t('knowledge.search.start')} description={t('knowledge.search.startHint')} />
          ) : found.isError ? (
            <ErrorState title={t('errors.generic')} description={message(found.error)} />
          ) : found.isLoading || !r ? (
            <Skeleton className="h-64" />
          ) : (
            <>
              {r.note && <p className="text-sm text-fg-3">{r.note}</p>}
              {r.status === 'indexing' ? (
                <EmptyState title={t('knowledge.search.indexing')} />
              ) : !r.items.length ? (
                <EmptyState title={t('knowledge.search.nothing')} />
              ) : (
                <ol className="grid gap-2" aria-label={t('knowledge.search.results')}>
                  {r.items.map((h) => (
                    <li key={h.id}>
                      <Card className="grid gap-1 p-3">
                        <span className="flex flex-wrap items-center gap-2">
                          <KindIcon kind={h.kind} sourceKind={h.source?.kind} mimeType={h.mimeType} />
                          <Link to={h.kind === 'item' ? '/knowledge/items/$id' : '/knowledge/documents/$id'} params={{ id: h.id }} className="font-bold hover:underline">
                            {h.title}
                          </Link>
                          {h.type && <TypeBadge type={h.type} />}
                          {h.validUntil && <Badge tone="warning">{t('knowledge.search.superseded')}</Badge>}
                          {h.kind === 'document' && h.url !== h.appUrl && (
                            <a href={h.url} target="_blank" rel="noopener noreferrer" className="text-fg-3 hover:text-fg-1" aria-label={t('knowledge.document.original')}>
                              <ExternalLink className="size-3.5" />
                            </a>
                          )}
                          <span className="ml-auto text-xs text-fg-3">{Math.round(h.score * 100)}%</span>
                        </span>
                        {h.heading && h.heading !== h.title && <span className="text-xs font-bold text-fg-2">{h.heading}</span>}
                        <Snippet text={h.snippet} className="text-sm text-fg-2" />
                        <span className="flex flex-wrap items-center gap-2 text-xs text-fg-3">
                          <SpaceLabel space={h.space} />
                          {h.source && <span>{h.source.name}</span>}
                          {h.path && <span>{h.path}</span>}
                          <span>{formatDate((h.eventDate ?? h.updatedAt).slice(0, 10))}</span>
                          {h.tags.map((tag) => (
                            <span key={tag}>#{tag}</span>
                          ))}
                        </span>
                      </Card>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </section>
        <aside className="grid content-start gap-4">
          <AskPanel scope={s.space ? { spaceId: s.space } : {}} />
        </aside>
      </div>
    </>
  );
}
